import { useLocation, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import AdminDetails from "../Admin/AdminDetails";
import NurseDetails from "../Nurse/NurseDetails";
import SurgeonDetails from "../Surgeon/SurgeonDetails";
import {
  useGetSurgeonDetailsQuery,
  useGetUserDetailsQuery,
  useGetUserRolesQuery,
} from "../../services/api";
import { RootState, useAppSelector } from "../../store";
import { resolveUserTypeFromRoleName } from "./userShared";

/** Route state shape for user details navigation. */
type UserDetailsState = {
  userType?: "NURSE" | "DOCTOR" | "ADMIN";
  healthOrgId?: number;
};

/**
 * Type guard for the user-details route state.
 *
 * Args:
 *   value: Unknown location state to validate.
 *
 * Returns:
 *   boolean: True when the state matches the expected shape.
 */
const isUserDetailsState = (value: unknown): value is UserDetailsState => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const maybeState = value as UserDetailsState;
  if (
    maybeState.healthOrgId !== undefined &&
    typeof maybeState.healthOrgId !== "number"
  ) {
    return false;
  }
  return (
    maybeState.userType === "NURSE" ||
    maybeState.userType === "DOCTOR" ||
    maybeState.userType === "ADMIN" ||
    maybeState.userType === undefined
  );
};

/**
 * Displays nurse, doctor, or admin details based on the selected user tab.
 *
 * Returns:
 *   JSX.Element: The details view for the resolved user type.
 *
 * Notes:
 *   Attempts to resolve user type from API data when the route state is missing.
 *   Returns null while resolving user type to avoid rendering an incorrect view.
 */
const UserDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const state = isUserDetailsState(location.state) ? location.state : null;
  const { userDetails } = useAppSelector(
    (authState: RootState) => authState.auth
  );
  const healthOrgId = userDetails?.health_org_id ?? state?.healthOrgId ?? 0;

  // When route state lacks user type, infer it from API data before rendering.
  const shouldResolveUserType = !state?.userType;
  const {
    data: userDetailsData,
    isFetching: isUserDetailsFetching,
  } = useGetUserDetailsQuery(shouldResolveUserType && id ? id : skipToken);
  const {
    data: rolesData,
    isFetching: isRolesFetching,
  } = useGetUserRolesQuery(
    shouldResolveUserType
      ? {
          is_active: true,
          is_detailed: true,
        }
      : skipToken
  );
  const {
    data: surgeonDetailsData,
    isFetching: isSurgeonDetailsFetching,
  } = useGetSurgeonDetailsQuery(
    shouldResolveUserType && id && healthOrgId
      ? { id, healthOrgId }
      : skipToken
  );

  // Role-name inference is a fallback path; surgeon details can override to DOCTOR.
  const roleName =
    rolesData?.items?.find(
      (role: { id?: number }) =>
        role?.id === userDetailsData?.user_role_id
    )?.name ?? "";
  const derivedUserType = resolveUserTypeFromRoleName(roleName);
  const resolvedUserType =
    state?.userType ?? (surgeonDetailsData ? "DOCTOR" : derivedUserType);
  // Avoid rendering a mismatched detail view while user type is still resolving.
  const isResolvingUserType =
    shouldResolveUserType &&
    !resolvedUserType &&
    (isUserDetailsFetching || isRolesFetching || isSurgeonDetailsFetching);

  if (isResolvingUserType) {
    return null;
  }
  const finalUserType = resolvedUserType ?? "NURSE";

  if (finalUserType === "DOCTOR") {
    return <SurgeonDetails />;
  }

  if (finalUserType === "ADMIN") {
    return <AdminDetails />;
  }

  return <NurseDetails />;
};

export default UserDetails;
